import { useCallback, useMemo } from 'react';

import AutoCompleteDropdown from 'components/uiLibrary/FormInputs/BaseComponents/AutoCompleteDropdown';
import LinkButton from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import useTracking from 'components/Globals/Analytics';
import { SUB_COMPONENTS } from 'components/Globals/Analytics/constants';
import CommonTrans from 'components/Globals/CommonTrans';

import { TP } from 'constants/index';

import { useAppliedProfessionFilter } from 'utils/globals/ssrQueries/hooks';
import useNavigate from 'utils/hooks/useNavigate';

import queries from 'containers/Artist/queries';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES, PROFESSION_TYPES } from 'operabase-router/constants';

import classes from './ArtistProfessionDropdown.module.scss';

const PROFESSION_TYPES_COUNT = Object.keys(PROFESSION_TYPES).length;

const LABELS = {
  [PROFESSION_TYPES.VOICE_TYPE]: `${TP}.FILTER_BY_VOICE_TYPES`,
  [PROFESSION_TYPES.INSTRUMENTALIST]: `${TP}.FN_FILTER_BY_INSTRUMENTALIST`,
  [PROFESSION_TYPES.PROFESSION]: `${TP}.FILTER_BY_PROFESSIONS`,
  [PROFESSION_TYPES.ROLE_REPERTOIRE]: `${TP}.m_ROLES`,
};

const getProfessionType = profession => {
  if (profession?.type) {
    if (profession?.type === PROFESSION_TYPES.VOICE_TYPE) {
      return PROFESSION_TYPES.VOICE_TYPE;
    }

    if (profession?.type === PROFESSION_TYPES.INSTRUMENTALIST) {
      return PROFESSION_TYPES.INSTRUMENTALIST;
    }
  }

  return PROFESSION_TYPES.PROFESSION;
};

export const PROFESSION_ROLE_TYPES = [
  PROFESSION_TYPES.VOICE_TYPE,
  PROFESSION_TYPES.PROFESSION,
  PROFESSION_TYPES.INSTRUMENTALIST,
  PROFESSION_TYPES.ROLE_REPERTOIRE,
];

const Label = ({ label, variant }) => {
  if (!variant) {
    return label;
  }

  return (
    <Typography variant={variant} size="11" truncate>
      <CommonTrans ns="NS_APP_GLOBALS" i18nKey={label} />
    </Typography>
  );
};

const ArtistProfessionDropdown = ({
  types = PROFESSION_ROLE_TYPES,
  className,
  labelVariant = 'span',
  variant,
  value,
  onChange,
  fullWidth,
  disablePortal,
  trackingData,
  isRoleRepertoire,
}) => {
  const track = useTracking();
  const navigate = useNavigate();

  const { t } = useTranslation('NS_APP_GLOBALS');

  const professionsQuery = useCallback(
    props => {
      // Determine which query to use based on the dropdown type
      let queryFunction;
      if (types.length === 1) {
        if (types[0] === PROFESSION_TYPES.VOICE_TYPE) {
          queryFunction = queries.getVoiceTypeProfessions;
        } else if (types[0] === PROFESSION_TYPES.INSTRUMENTALIST) {
          queryFunction = queries.getInstrumentalistProfessions;
        } else if (types[0] === PROFESSION_TYPES.PROFESSION) {
          queryFunction = queries.getGeneralProfessions;
        } else {
          queryFunction = queries.getArtistProfessions;
        }
      } else {
        // For mixed types or all types, use the original query with category splitting
        queryFunction = queries.getArtistProfessions;
      }

      return queryFunction({
        ...props,
        filters: {
          ...props.filters,
          is_searchable: true,
        },
        queryConfig: {
          ...props.queryConfig,
          ...(queryFunction === queries.getArtistProfessions && {
            // Only apply the category splitting for the original query
            select: response =>
              types.reduce((acc, type) => {
                acc.push(...(response?.data?.[type] || []));
                return acc;
              }, []),
          }),
        },
      });
    },
    [types],
  );

  const label = useMemo(() => {
    if (types.length < PROFESSION_TYPES_COUNT) {
      return types.map(type => t(LABELS[type])).join(' / ');
    }

    return t(LABELS[PROFESSION_TYPES.PROFESSION]);
  }, [t, types]);

  const selectedProfession = useAppliedProfessionFilter(value);
  const profession = useMemo(() => {
    // Since each dropdown now gets its own filtered data from the backend,
    // we can display the selected profession if it exists
    return selectedProfession;
  }, [selectedProfession]);

  const onChangeHandler = (e, newSelectedProfession) => {
    if (onChange) {
      onChange(e, newSelectedProfession);
    }

    track.click({
      ...trackingData,
      meta: {
        id: newSelectedProfession?.slug,
      },
    });
  };

  if (isRoleRepertoire) {
    return (
      <LinkButton
        variant="text"
        {...navigate.getLinkProps({
          entityType: ENTITY_TYPES.PROFILE,
          pro: true,
          tab: null,
        })}
        styles={{ root: classes.filterItem }}
        rightIcon={<SpriteIcon icon="open_in_new" size="16" className={classes.arrowIcon} />}
        isLink
        disableUnderline
        trackingData={{
          ...trackingData,
          subComponent: SUB_COMPONENTS.NAVIGATE_CTA,
        }}
      >
        <Typography variant={labelVariant} size="11" className={classes.filterText}>
          {labelVariant ? (
            <CommonTrans ns="NS_APP_GLOBALS" i18nKey={t(LABELS[PROFESSION_TYPES.ROLE_REPERTOIRE])} />
          ) : (
            t(LABELS[PROFESSION_TYPES.ROLE_REPERTOIRE])
          )}
        </Typography>
      </LinkButton>
    );
  }

  return (
    <AutoCompleteDropdown
      className={className}
      value={profession}
      onChange={onChangeHandler}
      searchQuery={professionsQuery}
      queryKey="aggregation_query"
      fullWidth={fullWidth}
      variant={variant}
      disablePortal={disablePortal}
      {...(!profession && {
        label: <Label label={label} variant={labelVariant} />,
      })}
    />
  );
};

export default ArtistProfessionDropdown;
